import { IBlockchain, SupportedNetworks } from './blockchain';
import { Address, beginCell, JettonMaster, toNano, TonClient } from '@ton/ton';
import TonConnect, { Wallet } from '@tonconnect/sdk';
import { TONContracts } from './TONContracts';
import Big from 'big.js';

declare const TonWeb: any;

export class TonBlockchain implements IBlockchain {

  walletInfo!: Wallet;
  connector!: TonConnect;
  private batchTransferIds: number[] = [];
  private batchTransferUnderXTon: number[] = [];

  constructor() {
    this.connector = new TonConnect({
      manifestUrl: `https://cdn.nesoex.com/nesoex-tonconnect-manifest.json?date=${new Date().getTime()}`
    });
    const unsubscribe = this.connector.onStatusChange(
      walletInfo => {
        // update state/reactive variables to show updates in the ui
        if (walletInfo) {
          this.walletInfo = walletInfo;
        }
      }
    );
  }

  addIDToTransferList(...ids: number[]) {
    for (const id of ids) {
      if (!this.batchTransferIds.find(_id => _id === id)) {
        this.batchTransferIds.push(id);
      }
    }
  }

  checkValidAddress() {

  }

  idExistInsideList(id: number) {
    return !!this.batchTransferIds.find(_id => _id === id);
  }

  removeIDFromTransferList(...ids: number[]) {
    for (const id of ids) {
      this.batchTransferIds = this.batchTransferIds.filter(_id => _id !== id);
    }
  }

  getBatchTransferIds() {
    return this.batchTransferIds;
  }


  async connect(): Promise<void> {
    const walletConnectionSource = {
      jsBridgeKey: 'tonkeeper'
    };
    if (!this.connector.connected) {
      this.connector.connect(walletConnectionSource);
      return new Promise((resolve, reject) => {
        const unsubscribe = this.connector.onStatusChange(
          walletInfo => {
            // update state/reactive variables to show updates in the ui
            if (walletInfo) {
              this.walletInfo = walletInfo;
              unsubscribe();
              resolve();
            } else {
              unsubscribe();
              reject('Wallet Not Connected');
            }
          }
        );
      });
    }
  }

  async sendTransaction(toAddress: string, amount: string, memo?: string): Promise<string> {
    let body = undefined;
    let transaction = undefined;
    if (memo) {
      body = beginCell()
        .storeUint(0, 32) // write 32 zero bits to indicate that a text comment will follow
        .storeStringTail(memo) // write our text comment
        .endCell();
      transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 3600, // 60 sec
        messages: [
          {
            address: toAddress,
            amount: toNano(amount).toString(),
            payload: body.toBoc().toString('base64')
          }
        ]
      };
    } else {
      transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 3600, // 60 sec
        messages: [
          {
            address: toAddress,
            amount: toNano(amount).toString()
          }
        ]
      };
    }
    const response = await this.connector.sendTransaction(transaction);
    const bocCell = TonWeb.boc.Cell.oneFromBoc(TonWeb.utils.base64ToBytes(response.boc));
    return TonWeb.utils.bytesToBase64(await bocCell.hash());
  }

  checkAddressIsValid(address: string) {
    try {
      Address.parse(address);
      return true;
    } catch (err) {
      return false;
    }
  }

  async sendToken(toAddress: string, tokenId: string, amount: string, network: SupportedNetworks, memo?: string): Promise<string> {
    let memoBody = undefined;
    let body = undefined;

    const client = new TonClient({
      endpoint: 'https://toncenter.com/api/v2/jsonRPC'
    });
    if (tokenId !== 'USDT' && tokenId !== 'NOT') {
      throw new Error('Contract address is not defined');
    }
    const masterAddress = TONContracts[tokenId].MasterAddress;
    const tokenDecimal = TONContracts[tokenId].decimal;


    const jettonMasterAddress = Address.parse(masterAddress);
    const userAddress = Address.parse(this.walletInfo.account.address);

    const jettonMaster = client.open(JettonMaster.create(jettonMasterAddress));
    const contractWalletAddress = await jettonMaster.getWalletAddress(userAddress);

    if (memo) {
      memoBody = beginCell()
        .storeUint(0, 32) // 0 opcode means we have a comment
        .storeStringTail(memo)
        .endCell();

      body = beginCell()
        .storeUint(0x0f8a7ea5, 32) // opcode for jetton transfer
        .storeUint(0, 64) // query id
        .storeCoins(Big(amount).mul(Big(10).pow(tokenDecimal)).toNumber()) // jetton amount, amount * 10^9
        .storeAddress(Address.parse(toAddress))           // destination:MsgAddress
        .storeAddress(Address.parse(this.walletInfo.account.address))              // response_destination:MsgAddress
        .storeBit(0) // no custom payload
        .storeCoins(toNano('0.05')) // forward amount (if >0, will send notification message)
        .storeBit(1) // we store forwardPayload as a reference
        .storeRef(memoBody)
        .endCell();

    } else {
      body = beginCell()
        .storeUint(0xf8a7ea5, 32)                 // Jetton transfer op code
        .storeUint(0, 64)                         // query_id:uint64
        .storeCoins(Big(amount).mul(Big(10).pow(tokenDecimal)).toNumber())                       // amount:(VarUInteger 16) - Jetton amount for transfer
        .storeAddress(Address.parse(toAddress))           // destination:MsgAddress
        .storeAddress(Address.parse(this.walletInfo.account.address))              // response_destination:MsgAddress
        .storeUint(0, 1)                          // custom_payload:(Maybe ^Cell)
        .storeCoins(toNano('0.05'))                 // forward_ton_amount:(VarUInteger 16)
        .storeUint(0, 1)                          // forward_payload:(Either Cell ^Cell)
        .endCell();
    }


    const response = await this.connector.sendTransaction({
      validUntil: Math.floor(Date.now() / 1000) + 3600,
      messages: [
        {
          address: contractWalletAddress.toString(),            // sender jetton wallet
          amount: toNano('1').toString(),                   // for commission fees, excess will be returned
          payload: body?.toBoc().toString('base64')  // payload with jetton transfer body
        }
      ]
    });
    const bocCell = TonWeb.boc.Cell.oneFromBoc(TonWeb.utils.base64ToBytes(response.boc));
    return TonWeb.utils.bytesToBase64(await bocCell.hash());
  }

  checkIsNativeToken(id: string, network: string): boolean {
    switch (network) {
      case 'TON':
        return id === 'TON';
      default:
        return false;
    }
  }

  isNetworkSupported(networkId: string): boolean {
    if (networkId === 'TON') {
      return true;
    }
    return false;
  }
}
